import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ActionButton, Buttons, CancelButton, Checkbox, Drawer, Form, FormMessage } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useAuth, useDirtyCheck, useForm } from '~/hooks';

const UL = styled.ul`
  li:not(:first-child) {
    margin-top: 0.75rem;
  }
`;

export default function WidgetSettings({ onClose, onSaved }) {
  const api = useApi();
  const auth = useAuth();
  const { workspace } = useWorkspace();

  const widgets = useMemo(
    () =>
      [
        { key: 'services_revenue', name: 'Services Revenue', hidden: !auth.executiveDashboard.servicesRevenue },
        {
          key: 'services_gross_profit',
          name: 'Services Gross Profit',
          hidden: !auth.executiveDashboard.servicesGrossProfit,
        },
        {
          key: 'services_gross_margin',
          name: 'Services Gross Margin',
          hidden: !auth.executiveDashboard.servicesGrossMargin,
        },
        {
          key: 'billable_utilization',
          name: 'Billable Utilization',
          hidden: !auth.executiveDashboard.billableUtilization,
        },
        {
          key: 'top_clients_by_services_revenue',
          name: 'Top Clients by Services Revenue',
          hidden: !auth.executiveDashboard.topClientsByServicesRevenue,
        },
        {
          key: 'top_clients_by_billable_hours',
          name: 'Top Clients by Billable Hours',
          hidden: !auth.executiveDashboard.topClientsByBillableHours,
        },
        {
          key: 'services_effective_bill_rate',
          name: 'Services Effective Bill Rate',
          hidden: !auth.executiveDashboard.servicesEffectiveBillRate,
        },
        {
          key: 'services_realization_rate',
          name: 'Services Realization Rate',
          hidden: !auth.executiveDashboard.servicesRealizationRate,
        },
        {
          key: 'client_billable_hours',
          name: 'Client Billable Hours',
          hidden: !auth.executiveDashboard.clientBillableHours,
        },
        {
          key: 'client_non_billable_hours',
          name: 'Client Non-Billable Hours',
          hidden: !auth.executiveDashboard.clientNonBillableHours,
        },
        {
          key: 'most_healthy_projects',
          name: 'Most Healthy Projects',
          hidden: !auth.executiveDashboard.mostHealthyProjects,
        },
        {
          key: 'least_healthy_projects',
          name: 'Least Healthy Projects',
          hidden: !auth.executiveDashboard.leastHealthyProjects,
        },
        {
          key: 'pipeline_by_expected_close_date',
          name: 'Pipeline By Expected Close Date',
          hidden: !auth.executiveDashboard.pipelineByExpectedCloseDate,
        },
      ].filter(({ hidden }) => !hidden),
    [auth],
  );

  const [memberWidgets, setMemberWidgets] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => isDirty);
  const [{ status, message, isSubmitting, saved }, form] = useForm();

  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).executiveDashboard().widgets().get();
      setMemberWidgets(data);
    })();
  }, [api, workspace.id]);

  const handleSubmit = async () => {
    try {
      form.submit();
      await api.www
        .workspaces(workspace.id)
        .executiveDashboard()
        .widgets()
        .save(
          widgets.map((widget) => ({ key: widget.key, visible: memberWidgets.some(({ key }) => key === widget.key) })),
        );
      await onSaved();
      form.done();
    } catch (error) {
      form.error({ message: error.message });
    }
  };

  return (
    <Drawer
      isOpen
      title="Personalize Dashboard"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {status && <FormMessage.Error>{message}</FormMessage.Error>}

            <Form.Section title="Dashboard Widgets">
              <UL>
                {widgets.map((widget) => {
                  const checked = memberWidgets?.some(({ key }) => key === widget.key) ?? false;

                  return (
                    <li key={widget.key}>
                      <Checkbox
                        label={widget.name}
                        checked={checked}
                        onChange={({ target: { checked } }) => {
                          setMemberWidgets((prev) => {
                            if (checked) {
                              return [...prev, widget];
                            } else {
                              return prev.filter(({ key }) => key !== widget.key);
                            }
                          });
                          setIsDirty(true);
                        }}
                      />
                    </li>
                  );
                })}
              </UL>
            </Form.Section>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} type="submit" onClick={() => handleSubmit()}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </div>
        );
      }}
    </Drawer>
  );
}
